import { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as Cesium from 'cesium';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LayersIcon from '@mui/icons-material/Layers';
import CloseIcon from '@mui/icons-material/Close';
import { COLORS, TOOLTIP_STYLES } from '../styles/viewerStyles';
import { ScaleBar } from './ScaleBar';

// Roads first = default; Aerial second
const BASEMAPS = [
  { assetId: 4 },  // Bing Maps Road (default)
  { assetId: 2 },  // Bing Maps Aerial with Labels
];

const MINIMAP_FPS = 60;
const FRUSTUM_DEPTH_RATIO = 0.12;
const MIN_SIZE = 160;

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * Derive an axis-aligned bounding rectangle from the tileset's bounding sphere.
 * Uses the inscribed square (half-extent = r / √2) so the box fits inside the sphere.
 */
function computeTilesetRect(tileset) {
  if (!tileset || !tileset.boundingSphere) return null;
  const bs = tileset.boundingSphere;
  if (bs.radius <= 0) return null;

  const center = Cesium.Cartographic.fromCartesian(bs.center);
  if (!isFinite(center.longitude) || !isFinite(center.latitude)) return null;

  const lonDeg = Cesium.Math.toDegrees(center.longitude);
  const latDeg = Cesium.Math.toDegrees(center.latitude);
  const inscribed = bs.radius / Math.SQRT2;
  const mPerDegLat = 111320;
  const mPerDegLon = 111320 * Math.cos(center.latitude);
  const dLat = inscribed / mPerDegLat;
  const dLon = inscribed / (mPerDegLon || 1);

  return { west: lonDeg - dLon, south: latDeg - dLat, east: lonDeg + dLon, north: latDeg + dLat };
}

/**
 * Compute the 5 pyramid vertices using the main camera's actual direction/up/right
 * vectors, then project to lat/lon (altitude = 0) for SCENE2D rendering.
 */
function computePyramidPositions(mainViewer, frustumDepth) {
  try {
    const camera = mainViewer.camera;
    const pos = camera.position;
    if (!isFinite(pos.x) || !isFinite(pos.y) || !isFinite(pos.z)) return null;

    let fov = Cesium.Math.toRadians(45);
    try {
      if (camera.frustum && isFinite(camera.frustum.fov)) {
        fov = Math.min(camera.frustum.fov, Cesium.Math.toRadians(90));
      }
    } catch (_) { /* keep default */ }

    const halfH = Math.tan(fov * 0.5) * frustumDepth;
    const halfW = halfH * (4 / 3);

    const C3 = Cesium.Cartesian3;
    const add = (a, b) => C3.add(a, b, new C3());
    const sub = (a, b) => C3.subtract(a, b, new C3());
    const scl = (v, s) => C3.multiplyByScalar(v, s, new C3());

    const baseCenter = add(C3.clone(pos), scl(camera.direction, frustumDepth));
    const rOff = scl(camera.right, halfW);
    const uOff = scl(camera.up, halfH);

    const pts3d = {
      apex: C3.clone(pos),
      bl: sub(sub(baseCenter, rOff), uOff),
      br: add(sub(baseCenter, rOff), uOff),
      tr: add(add(baseCenter, rOff), uOff),
      tl: sub(add(baseCenter, rOff), uOff),
    };

    const project = (c) => {
      const carto = Cesium.Cartographic.fromCartesian(c);
      if (!isFinite(carto.longitude) || !isFinite(carto.latitude)) return null;
      return Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, 0);
    };

    const result = {};
    for (const [k, v] of Object.entries(pts3d)) {
      result[k] = project(v);
      if (!result[k]) return null;
    }
    return result;
  } catch (_) {
    return null;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function MinimapViewer({
  mainViewer, tileset, assetId, onClose,
  selectedProperty, intensityRange,
}) {
  const { t } = useTranslation();

  // ── Drag / resize state ───────────────────────────────────────────────────
  const [pos,  setPos]  = useState(() => ({
    top:  12,
    left: window.innerWidth - 12 - 300,
  }));
  const [size, setSize] = useState({ width: 300, height: 300 });

  // Always-current refs so event-handler closures never see stale values
  const posRef  = useRef(pos);
  const sizeRef = useRef(size);
  useEffect(() => { posRef.current  = pos;  }, [pos]);
  useEffect(() => { sizeRef.current = size; }, [size]);

  // ── Drag ──────────────────────────────────────────────────────────────────
  const startDrag = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();

    const { top, left } = posRef.current;
    const ox = e.clientX - left;
    const oy = e.clientY - top;

    const onMove = (e) => {
      const { width, height } = sizeRef.current;
      setPos({
        left: Math.max(0, Math.min(window.innerWidth  - width,  e.clientX - ox)),
        top:  Math.max(0, Math.min(window.innerHeight - height, e.clientY - oy)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, []);

  // ── Resize ────────────────────────────────────────────────────────────────
  const startResize = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const { width, height } = sizeRef.current;
    const { top, left } = posRef.current;
    const sx = e.clientX;
    const sy = e.clientY;

    const onMove = (e) => {
      // Grip is bottom-left: dragging left → grow width + move left anchor
      const dx = sx - e.clientX;
      const dy = e.clientY - sy;
      const newW = Math.max(MIN_SIZE, width  + dx);
      const newH = Math.max(MIN_SIZE, height + dy);
      // Adjust left so the right edge stays fixed
      const clampedDx = width - newW; // actual dx after clamping
      setSize({ width: newW, height: newH });
      setPos({ top, left: left + clampedDx });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, []);

  // ── Cesium refs ───────────────────────────────────────────────────────────
  const containerRef          = useRef(null);
  const miniViewerRef         = useRef(null);
  const [miniViewerReady, setMiniViewerReady] = useState(null);
  const mainViewerRef         = useRef(mainViewer);
  const tilesetRef            = useRef(tileset);
  const minimapTilesetRef     = useRef(null);
  const basemapIndexRef       = useRef(0);
  const [basemapIndex, setBasemapIndex] = useState(0);

  // Prop refs — keep always-current copies so async callbacks get fresh values
  const selectedPropertyRef = useRef(selectedProperty);
  const intensityRangeRef   = useRef(intensityRange);

  useEffect(() => { mainViewerRef.current       = mainViewer;       }, [mainViewer]);
  useEffect(() => { tilesetRef.current          = tileset;          }, [tileset]);
  useEffect(() => { selectedPropertyRef.current = selectedProperty; }, [selectedProperty]);
  useEffect(() => { intensityRangeRef.current   = intensityRange;   }, [intensityRange]);

  // ── Cesium init ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    let miniViewer;

    try {
      miniViewer = new Cesium.Viewer(containerRef.current, {
        sceneMode: Cesium.SceneMode.SCENE2D,
        animation: false,
        timeline: false,
        baseLayerPicker: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        navigationInstructionsInitiallyVisible: false,
        fullscreenButton: false,
        infoBox: false,
        selectionIndicator: false,
      });

      miniViewer.cesiumWidget.creditContainer.style.display = 'none';
      miniViewer.resolutionScale = Math.min(window.devicePixelRatio || 1, 3);

      const ctrl = miniViewer.scene.screenSpaceCameraController;
      ctrl.enableRotate    = false;
      ctrl.enableTranslate = false;
      ctrl.enableZoom      = false;
      ctrl.enableTilt      = false;
      ctrl.enableLook      = false;

      miniViewer.targetFrameRate = MINIMAP_FPS;
      miniViewerRef.current = miniViewer;
      setMiniViewerReady(miniViewer);

      // Default basemap: Roads
      const base0 = miniViewer.imageryLayers.get(0);
      if (base0) miniViewer.imageryLayers.remove(base0);
      Cesium.IonImageryProvider.fromAssetId(BASEMAPS[0].assetId)
        .then(provider => {
          if (!miniViewer || miniViewer.isDestroyed()) return;
          miniViewer.imageryLayers.addImageryProvider(provider, 0);
        })
        .catch(e => console.warn('MinimapViewer: initial basemap failed:', e));

      // Frustum pyramid fill
      miniViewer.entities.add({
        polygon: {
          hierarchy: new Cesium.CallbackProperty(() => {
            const mv = mainViewerRef.current;
            if (!mv || mv.isDestroyed()) return new Cesium.PolygonHierarchy([]);
            const depth = miniViewer.camera.positionCartographic.height * FRUSTUM_DEPTH_RATIO;
            const pts = computePyramidPositions(mv, depth);
            if (!pts) return new Cesium.PolygonHierarchy([]);
            return new Cesium.PolygonHierarchy([pts.bl, pts.br, pts.tr, pts.tl]);
          }, false),
          material: Cesium.Color.WHITE.withAlpha(0.50),
          fill: true,
          outline: false,
        },
      });

            // Frustum pyramid fill
      miniViewer.entities.add({
        polygon: {
          hierarchy: new Cesium.CallbackProperty(() => {
            const mv = mainViewerRef.current;
            if (!mv || mv.isDestroyed()) return new Cesium.PolygonHierarchy([]);
            const depth = miniViewer.camera.positionCartographic.height * FRUSTUM_DEPTH_RATIO;
            const pts = computePyramidPositions(mv, depth);
            if (!pts) return new Cesium.PolygonHierarchy([]);
            const { apex, bl, br, tr, tl } = pts;
            return new Cesium.PolygonHierarchy([apex,bl,br,apex,br,tr,apex,tr,tl]);
          }, false),
          material: Cesium.Color.WHITE.withAlpha(0.40),
          fill: true,
          outline: false,
        },
      });

      // Frustum pyramid wireframe
      miniViewer.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            const mv = mainViewerRef.current;
            if (!mv || mv.isDestroyed()) return [];
            const depth = miniViewer.camera.positionCartographic.height * FRUSTUM_DEPTH_RATIO;
            const pts = computePyramidPositions(mv, depth);
            if (!pts) return [];
            const { apex, bl, br, tr, tl } = pts;
            return [bl, br, tr, tl, bl, apex, br, apex, tr, apex, tl];
          }, false),
          width: 1,
          material: Cesium.Color.BLACK.withAlpha(0.50),
          arcType: Cesium.ArcType.NONE,
          clampToGround: false,
        },
      });

      // Auto-zoom: fit dataset bounding sphere; fall back to main camera position
      const bs = tileset?.boundingSphere;
      if (bs && bs.radius > 0) {
        const center = Cesium.Cartographic.fromCartesian(bs.center);
        if (isFinite(center.longitude) && isFinite(center.latitude)) {
          miniViewer.camera.setView({
            destination: Cesium.Cartesian3.fromDegrees(
              Cesium.Math.toDegrees(center.longitude),
              Cesium.Math.toDegrees(center.latitude),
              bs.radius * 6,
            ),
          });
        }
      } else {
        const mainCarto = mainViewer.camera.positionCartographic;
        if (isFinite(mainCarto.longitude) && isFinite(mainCarto.latitude)) {
          miniViewer.camera.setView({
            destination: Cesium.Cartesian3.fromDegrees(
              Cesium.Math.toDegrees(mainCarto.longitude),
              Cesium.Math.toDegrees(mainCarto.latitude),
              Math.max(mainCarto.height * 10, 2000),
            ),
          });
        }
      }

      // Camera centre sync — fires before every minimap render frame so the
      // viewport is always in sync with the cone indicator (no drift/shaking).
      const syncCamera = () => {
        const mv = mainViewerRef.current;
        if (!mv || mv.isDestroyed() || miniViewer.isDestroyed()) return;
        const carto = mv.camera.positionCartographic;
        if (!isFinite(carto.longitude) || !isFinite(carto.latitude)) return;
        miniViewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(
            Cesium.Math.toDegrees(carto.longitude),
            Cesium.Math.toDegrees(carto.latitude),
            miniViewer.camera.positionCartographic.height,
          ),
        });
      };
      syncCamera();
      miniViewer.scene.preRender.addEventListener(syncCamera);

    } catch (error) {
      console.warn('MinimapViewer: initialization failed:', error);
    }

    return () => {
      if (miniViewerRef.current && !miniViewerRef.current.isDestroyed()) {
        try { miniViewerRef.current.destroy(); } catch (_) { /* noop */ }
      }
      miniViewerRef.current = null;
      // Do NOT call setMiniViewerReady(null) here. The component is already
      // unmounting so the state update is redundant — and calling it after
      // destroying the viewer triggers a second ScaleBar unmount that runs
      // its cleanup against an already-destroyed viewer, causing a crash.
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load actual tileset into minimap ──────────────────────────────────────
  useEffect(() => {
    const mini = miniViewerRef.current;
    if (!mini || mini.isDestroyed()) return;

    // Remove any previously loaded minimap tileset
    const prev = minimapTilesetRef.current;
    if (prev) {
      try { if (!mini.isDestroyed()) mini.scene.primitives.remove(prev); } catch (_) {}
      minimapTilesetRef.current = null;
    }

    if (!assetId) return;

    let cancelled = false;
    Cesium.Cesium3DTileset.fromIonAssetId(assetId, { maximumScreenSpaceError: 64 })
      .then(miniTileset => {
        if (cancelled) {
          try { miniTileset.destroy(); } catch (_) {}
          return;
        }
        const m = miniViewerRef.current;
        if (!m || m.isDestroyed()) {
          try { miniTileset.destroy(); } catch (_) {}
          return;
        }
        minimapTilesetRef.current = miniTileset;
        m.scene.primitives.add(miniTileset);

        // Apply visualization shader — supports intensity (grayscale) and default RGB color.
        // Elevation and distance modes are not supported in SCENE2D (positionWC is not
        // reliable in the 2D projection), so we intentionally omit them.
        const sp = selectedPropertyRef.current;
        const ir = intensityRangeRef.current;
        const mu = tilesetRef.current?.customShader?.uniforms;

        miniTileset.customShader = new Cesium.CustomShader({
          lightingModel: Cesium.LightingModel.UNLIT,
          uniforms: {
            u_pointSize:      { type: Cesium.UniformType.FLOAT, value: 3.0 },
            u_useIntensity:   { type: Cesium.UniformType.BOOL,  value: sp === 'intensity' },
            u_hasIntensity:   { type: Cesium.UniformType.BOOL,  value: mu?.u_hasIntensity?.value ?? true },
            u_intensityRange: { type: Cesium.UniformType.VEC2,  value: Array.isArray(ir) ? new Cesium.Cartesian2(ir[0], ir[1]) : new Cesium.Cartesian2(0, 255) },
          },
          vertexShaderText: `
void vertexMain(VertexInput vsInput, inout czm_modelVertexOutput vsOutput) {
  vsOutput.pointSize = u_pointSize;
}`,
          fragmentShaderText: `
void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
  if (u_useIntensity && u_hasIntensity) {
    float range = max(u_intensityRange.y - u_intensityRange.x, 1e-6);
    float ni = (fsInput.attributes.intensity - u_intensityRange.x) / range;
    material.diffuse = vec3(clamp(ni, 0.0, 1.0));
  }
  // else: keep default per-point RGB color
}`,
        });
      })
      .catch(e => console.warn('MinimapViewer: tileset load failed:', e));

    return () => {
      cancelled = true;
      const loaded = minimapTilesetRef.current;
      const m = miniViewerRef.current;
      if (loaded && m && !m.isDestroyed()) {
        try { m.scene.primitives.remove(loaded); } catch (_) {}
      }
      minimapTilesetRef.current = null;
    };
  }, [assetId]);

  // ── Sync intensity uniforms from props ────────────────────────────────────
  useEffect(() => {
    const miniTs = minimapTilesetRef.current;
    if (!miniTs?.customShader) return;
    miniTs.customShader.setUniform('u_useIntensity', selectedProperty === 'intensity');
    if (Array.isArray(intensityRange)) {
      miniTs.customShader.setUniform('u_intensityRange', new Cesium.Cartesian2(intensityRange[0], intensityRange[1]));
    }
  }, [selectedProperty, intensityRange]);

  // ── Basemap / zoom controls ───────────────────────────────────────────────
  const handleCycleBasemap = useCallback(() => {
    const mini = miniViewerRef.current;
    if (!mini || mini.isDestroyed()) return;
    const nextIdx = (basemapIndexRef.current + 1) % BASEMAPS.length;
    basemapIndexRef.current = nextIdx;
    setBasemapIndex(nextIdx);
    const { assetId: basemapId } = BASEMAPS[nextIdx];
    const baseLayer = mini.imageryLayers.get(0);
    if (baseLayer) mini.imageryLayers.remove(baseLayer);
    try {
      const p = Cesium.IonImageryProvider.fromAssetId(basemapId);
      if (p && typeof p.then === 'function') {
        p.then(provider => {
          if (!mini || mini.isDestroyed()) return;
          mini.imageryLayers.addImageryProvider(provider, 0);
        }).catch(e => console.warn('MinimapViewer: basemap load failed:', e));
      } else {
        mini.imageryLayers.addImageryProvider(p, 0);
      }
    } catch (e) {
      try {
        mini.imageryLayers.addImageryProvider(new Cesium.IonImageryProvider({ assetId: basemapId }), 0);
      } catch (e2) {
        console.warn('MinimapViewer: basemap change failed:', e2);
      }
    }
  }, []);

  const handleZoomIn = useCallback(() => {
    const mini = miniViewerRef.current;
    if (!mini || mini.isDestroyed()) return;
    mini.camera.zoomIn(mini.camera.positionCartographic.height * 0.5);
  }, []);

  const handleZoomOut = useCallback(() => {
    const mini = miniViewerRef.current;
    if (!mini || mini.isDestroyed()) return;
    mini.camera.zoomOut(mini.camera.positionCartographic.height * 0.5);
  }, []);

  // ── Styles ────────────────────────────────────────────────────────────────
  const noStartDrag = (e) => e.stopPropagation();

  const overlaySx = {
    color: COLORS.white,
    width: 26,
    height: 26,
    borderRadius: '6px',
    bgcolor: 'rgba(20,20,30,0.72)',
    backdropFilter: 'blur(8px)',
    border: `1px solid ${COLORS.borderLight}`,
    '&:hover': { bgcolor: 'rgba(50,50,70,0.92)' },
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Grow in={true} timeout={250}>
    <Box sx={{
      position: 'absolute',
      top:    pos.top,
      left:   pos.left,
      width:  size.width,
      height: size.height,
      zIndex: 1150,
      bgcolor: COLORS.darkBg,
      backdropFilter: 'blur(16px)',
      border: `1px solid ${COLORS.panelBorder}`,
      boxShadow: `${COLORS.panelShadow}, inset 0 0 20px rgba(255,255,255,0.06), inset 0 0 1px rgba(255,255,255,0.1)`,
      borderRadius: '12px',
      overflow: 'hidden',
      userSelect: 'none',
    }}>

      {/* Cesium 2D canvas */}
      <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />

      {/* Frosted inner-edge vignette */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '12px',
          boxShadow: 'inset 0 0 30px rgba(0,0,0,0.15), inset 0 0 2px rgba(255,255,255,0.08)',
          pointerEvents: 'none',
          zIndex: 4,
        }}
      />

      {/* Scale bar — bottom-left corner, above canvas, below controls */}
      {miniViewerReady && (
        <ScaleBar
          viewer={miniViewerReady}
          sx={{ bottom: 8, left: 8, zIndex: 6 }}
          color={basemapIndex === 1 ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'}
        />
      )}

      {/* Drag overlay — covers the canvas, passes clicks through to buttons above */}
      <Box
        onMouseDown={startDrag}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 5,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
        }}
      />

      {/* Close button — upper-right */}
      {onClose && (
        <Tooltip title={t('Close')} placement="left" arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <IconButton
            size="small"
            onClick={onClose}
            onMouseDown={noStartDrag}
            sx={{
              ...overlaySx,
              position: 'absolute',
              top: 6,
              right: 6,
              width: 22,
              height: 22,
              zIndex: 11,
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* 3 overlay buttons — right-side, vertically centred */}
      <Box
        onMouseDown={noStartDrag}
        sx={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          zIndex: 10,
        }}
      >
        <Tooltip title={t('BaseLayers')} placement="left" arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <IconButton size="small" onClick={handleCycleBasemap} sx={overlaySx}>
            <LayersIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('ZoomIn')} placement="left" arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <IconButton size="small" onClick={handleZoomIn} sx={overlaySx}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('ZoomOut')} placement="left" arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <IconButton size="small" onClick={handleZoomOut} sx={overlaySx}>
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Resize handle — bottom-left corner */}
      <Box
        onMouseDown={startResize}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: 18,
          height: 18,
          zIndex: 11,
          cursor: 'nesw-resize',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
          pb: '3px',
          pl: '3px',
        }}
      >
        <svg width="10" height="10" style={{ display: 'block', transform: 'rotate(90deg)' }}>
          <line x1="1" y1="9" x2="9" y2="1" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="4" y1="9" x2="9" y2="4" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </Box>

    </Box>
    </Grow>
  );
}

MinimapViewer.propTypes = {
  mainViewer:       PropTypes.object,
  tileset:          PropTypes.object,
  assetId:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose:          PropTypes.func,
  selectedProperty: PropTypes.string,
  intensityRange:   PropTypes.array,
};
